import * as z from 'zod';

import {
  CompanyId,
  CompanyReportId,
  EventId,
  InvestorReportingId,
  LoanId,
  RecurringCompanyReportId,
  UserId,
} from '../BrandedIds';
import { CompanyReportType } from '../CompanyReport';
import { GSEName } from '../loan/Loan';
import { ReportingCycle } from '../loan/NonPerformanceCodeForReportingCycle';
import { zodDateOrString } from '../utils';
import { FannieMaeCRS } from './FannieMae/FannieMaeCRS';
import { FannieMaeDelinquencyFile } from './FannieMae/FannieMaeDelinquencyFile';
import { FannieMaeLar32 } from './FannieMae/FannieMaeLar32';
import { FannieMaeLar81 } from './FannieMae/FannieMaeLar81';
import { FannieMaeLar83 } from './FannieMae/FannieMaeLar83';
import { FannieMaeLar89 } from './FannieMae/FannieMaeLar89';
import { FannieMaeLar96 } from './FannieMae/FannieMaeLar96';
import { FHATS264 } from './FHA/FHATS264';
import { FreddieMacEDR } from './FreddieMac/FreddieMacEDR';
import { FreddieMacLLR } from './FreddieMac/FreddieMacLLR';
import { GinnieMaeRFS } from './GinnieMae/GinnieMaeRFS';

export const InvestorReportingReportType = z.enum([
  CompanyReportType.FANNIE_MAE_LAR_96_DAILY,
  CompanyReportType.FANNIE_MAE_LAR_96_MONTHLY,
  CompanyReportType.FANNIE_MAE_LAR_96_TRAILING,
  CompanyReportType.FANNIE_MAE_CRS_DRAFT_REQUEST,
  CompanyReportType.FANNIE_MAE_CRS_DRAFT_REQUEST_EDIT,
  CompanyReportType.FANNIE_MAE_LAR_32,
  CompanyReportType.FANNIE_MAE_LAR_32_EDIT,
  CompanyReportType.FANNIE_MAE_LAR_81,
  CompanyReportType.FANNIE_MAE_LAR_81_EDIT,
  CompanyReportType.FANNIE_MAE_LAR_83,
  CompanyReportType.FANNIE_MAE_LAR_83_EDIT,
  CompanyReportType.FANNIE_MAE_LAR_89,
  CompanyReportType.FANNIE_MAE_LAR_89_EDIT,
  CompanyReportType.FANNIE_MAE_LAR_96_EDIT,
  CompanyReportType.FANNIE_MAE_DELINQUENCY,
  CompanyReportType.FANNIE_MAE_DELINQUENCY_EDIT,

  CompanyReportType.FREDDIE_MAC_LLR_DAILY,
  CompanyReportType.FREDDIE_MAC_LLR_MONTHLY,
  CompanyReportType.FREDDIE_MAC_LLR_TRAILING,
  CompanyReportType.FREDDIE_MAC_LLR_EDIT,
  CompanyReportType.FREDDIE_MAC_EDR,
  CompanyReportType.FREDDIE_MAC_EDR_EDIT,

  CompanyReportType.GINNIE_RFS,
  CompanyReportType.GINNIE_RFS_EDIT,

  CompanyReportType.FHA_TS_264,
  CompanyReportType.FHA_TS_264_EDIT,
]);
export type InvestorReportingReportType = z.infer<typeof InvestorReportingReportType>;

export const InvestorReportingCompanyReportTypes: InvestorReportingReportType[] = [
  CompanyReportType.FANNIE_MAE_LAR_96_DAILY,
  CompanyReportType.FANNIE_MAE_LAR_96_MONTHLY,
  CompanyReportType.FANNIE_MAE_LAR_96_TRAILING,
  CompanyReportType.FANNIE_MAE_LAR_32,
  CompanyReportType.FANNIE_MAE_LAR_32_EDIT,
  CompanyReportType.FANNIE_MAE_LAR_81,
  CompanyReportType.FANNIE_MAE_LAR_81_EDIT,
  CompanyReportType.FANNIE_MAE_LAR_83,
  CompanyReportType.FANNIE_MAE_LAR_83_EDIT,
  CompanyReportType.FANNIE_MAE_LAR_89,
  CompanyReportType.FANNIE_MAE_LAR_89_EDIT,
  CompanyReportType.FANNIE_MAE_LAR_96_EDIT,
  CompanyReportType.FANNIE_MAE_CRS_DRAFT_REQUEST,
  CompanyReportType.FANNIE_MAE_CRS_DRAFT_REQUEST_EDIT,
  CompanyReportType.FANNIE_MAE_DELINQUENCY,
  CompanyReportType.FANNIE_MAE_DELINQUENCY_EDIT,

  CompanyReportType.FREDDIE_MAC_LLR_DAILY,
  CompanyReportType.FREDDIE_MAC_LLR_MONTHLY,
  CompanyReportType.FREDDIE_MAC_LLR_TRAILING,
  CompanyReportType.FREDDIE_MAC_LLR_EDIT,
  CompanyReportType.FREDDIE_MAC_EDR,
  CompanyReportType.FREDDIE_MAC_EDR_EDIT,

  CompanyReportType.GINNIE_RFS,
  CompanyReportType.GINNIE_RFS_EDIT,

  CompanyReportType.FHA_TS_264,
  CompanyReportType.FHA_TS_264_EDIT,
] as const;

export type FannieMaeLAR96ReportTypes =
  | CompanyReportType.FANNIE_MAE_LAR_96_DAILY
  | CompanyReportType.FANNIE_MAE_LAR_96_MONTHLY
  | CompanyReportType.FANNIE_MAE_LAR_96_TRAILING
  | CompanyReportType.FANNIE_MAE_LAR_96_EDIT;

export type FreddieMacLLRReportTypes =
  | CompanyReportType.FREDDIE_MAC_LLR_DAILY
  | CompanyReportType.FREDDIE_MAC_LLR_MONTHLY
  | CompanyReportType.FREDDIE_MAC_LLR_TRAILING
  | CompanyReportType.FREDDIE_MAC_LLR_EDIT;

export const InvestorReportingReportTypeToInvestor: Record<InvestorReportingReportType, GSEName> = {
  [CompanyReportType.FANNIE_MAE_LAR_96_DAILY]: 'FNMA',
  [CompanyReportType.FANNIE_MAE_LAR_96_MONTHLY]: 'FNMA',
  [CompanyReportType.FANNIE_MAE_LAR_96_TRAILING]: 'FNMA',
  [CompanyReportType.FANNIE_MAE_CRS_DRAFT_REQUEST]: 'FNMA',
  [CompanyReportType.FANNIE_MAE_CRS_DRAFT_REQUEST_EDIT]: 'FNMA',
  [CompanyReportType.FANNIE_MAE_LAR_32]: 'FNMA',
  [CompanyReportType.FANNIE_MAE_LAR_32_EDIT]: 'FNMA',
  [CompanyReportType.FANNIE_MAE_LAR_81]: 'FNMA',
  [CompanyReportType.FANNIE_MAE_LAR_81_EDIT]: 'FNMA',
  [CompanyReportType.FANNIE_MAE_LAR_83]: 'FNMA',
  [CompanyReportType.FANNIE_MAE_LAR_83_EDIT]: 'FNMA',
  [CompanyReportType.FANNIE_MAE_LAR_89]: 'FNMA',
  [CompanyReportType.FANNIE_MAE_LAR_89_EDIT]: 'FNMA',
  [CompanyReportType.FANNIE_MAE_LAR_96_EDIT]: 'FNMA',
  [CompanyReportType.FANNIE_MAE_DELINQUENCY]: 'FNMA',
  [CompanyReportType.FANNIE_MAE_DELINQUENCY_EDIT]: 'FNMA',

  [CompanyReportType.FREDDIE_MAC_LLR_DAILY]: 'FHLMC',
  [CompanyReportType.FREDDIE_MAC_LLR_MONTHLY]: 'FHLMC',
  [CompanyReportType.FREDDIE_MAC_LLR_TRAILING]: 'FHLMC',
  [CompanyReportType.FREDDIE_MAC_LLR_EDIT]: 'FHLMC',
  [CompanyReportType.FREDDIE_MAC_EDR]: 'FHLMC',
  [CompanyReportType.FREDDIE_MAC_EDR_EDIT]: 'FHLMC',

  [CompanyReportType.GINNIE_RFS]: 'GNMA',
  [CompanyReportType.GINNIE_RFS_EDIT]: 'GNMA',

  [CompanyReportType.FHA_TS_264]: 'FHA',
  [CompanyReportType.FHA_TS_264_EDIT]: 'FHA',
} as const;

export const InvestorReportingBalances = z.object({
  outstandingPrincipalTotal: z.number(),
  outstandingPrincipalInterestBearing: z.number(),
  outstandingPrincipalNonInterestBearing: z.number(),
  totalPaidPrincipal: z.number(),
  totalPaidInterest: z.number(),
  totalPaidEscrow: z.number(),
  totalPaidFee: z.number(),
  totalPaidAdvance: z.number(),
});
export type InvestorReportingBalances = z.infer<typeof InvestorReportingBalances>;

const FannieMaeLar96DailyReportData = z.object({
  reportType: z.literal(CompanyReportType.FANNIE_MAE_LAR_96_DAILY),
  data: FannieMaeLar96,
  balances: InvestorReportingBalances.optional(),
});

const FannieMaeLar96MonthlyReportData = z.object({
  reportType: z.literal(CompanyReportType.FANNIE_MAE_LAR_96_MONTHLY),
  data: FannieMaeLar96,
  balances: InvestorReportingBalances.optional(),
});

const FannieMaeLar96TrailingReportData = z.object({
  reportType: z.literal(CompanyReportType.FANNIE_MAE_LAR_96_TRAILING),
  data: FannieMaeLar96,
  balances: InvestorReportingBalances.optional(),
});

const FannieMaeLar96EditReportData = z.object({
  reportType: z.literal(CompanyReportType.FANNIE_MAE_LAR_96_EDIT),
  data: FannieMaeLar96,
  balances: InvestorReportingBalances.optional(),
});

const FannieMaeCRSDraftRequestReportData = z.object({
  reportType: z.literal(CompanyReportType.FANNIE_MAE_CRS_DRAFT_REQUEST),
  data: FannieMaeCRS,
});

const FannieMaeCRSDraftRequestEditReportData = z.object({
  reportType: z.literal(CompanyReportType.FANNIE_MAE_CRS_DRAFT_REQUEST_EDIT),
  data: FannieMaeCRS,
});

const FreddieMacLLRMonthlyReportData = z.object({
  reportType: z.literal(CompanyReportType.FREDDIE_MAC_LLR_MONTHLY),
  data: FreddieMacLLR,
  balances: InvestorReportingBalances.optional(),
});

const FreddieMacLLRDailyReportData = z.object({
  reportType: z.literal(CompanyReportType.FREDDIE_MAC_LLR_DAILY),
  data: FreddieMacLLR,
  balances: InvestorReportingBalances.optional(),
});

const FreddieMacLLRTrailingReportData = z.object({
  reportType: z.literal(CompanyReportType.FREDDIE_MAC_LLR_TRAILING),
  data: FreddieMacLLR,
  balances: InvestorReportingBalances.optional(),
});

const FreddieMacLLREditReportData = z.object({
  reportType: z.literal(CompanyReportType.FREDDIE_MAC_LLR_EDIT),
  data: FreddieMacLLR,
  balances: InvestorReportingBalances.optional(),
});

const FreddieMacEdrReportData = z.object({
  reportType: z.literal(CompanyReportType.FREDDIE_MAC_EDR),
  data: FreddieMacEDR,
});

const FreddieMacEdrEditReportData = z.object({
  reportType: z.literal(CompanyReportType.FREDDIE_MAC_EDR_EDIT),
  data: FreddieMacEDR,
});

const GinnieMaeRFSMonthlyReportData = z.object({
  reportType: z.literal(CompanyReportType.GINNIE_RFS),
  data: GinnieMaeRFS,
  balances: InvestorReportingBalances.optional(),
});

const GinnieMaeRFSEditReportData = z.object({
  reportType: z.literal(CompanyReportType.GINNIE_RFS_EDIT),
  data: GinnieMaeRFS,
  balances: InvestorReportingBalances.optional(),
});

const FannieMaeLar32ReportData = z.object({
  reportType: z.literal(CompanyReportType.FANNIE_MAE_LAR_32),
  data: FannieMaeLar32,
});

const FannieMaeLar32EditReportData = z.object({
  reportType: z.literal(CompanyReportType.FANNIE_MAE_LAR_32_EDIT),
  data: FannieMaeLar32,
});

const FannieMaeLar81ReportData = z.object({
  reportType: z.literal(CompanyReportType.FANNIE_MAE_LAR_81),
  data: FannieMaeLar81,
});

const FannieMaeLar81EditReportData = z.object({
  reportType: z.literal(CompanyReportType.FANNIE_MAE_LAR_81_EDIT),
  data: FannieMaeLar81,
});

const FannieMaeLar83ReportData = z.object({
  reportType: z.literal(CompanyReportType.FANNIE_MAE_LAR_83),
  data: FannieMaeLar83,
});

const FannieMaeLar83EditReportData = z.object({
  reportType: z.literal(CompanyReportType.FANNIE_MAE_LAR_83_EDIT),
  data: FannieMaeLar83,
});

const FannieMaeLar89ReportData = z.object({
  reportType: z.literal(CompanyReportType.FANNIE_MAE_LAR_89),
  data: FannieMaeLar89,
});

const FannieMaeLar89EditReportData = z.object({
  reportType: z.literal(CompanyReportType.FANNIE_MAE_LAR_89_EDIT),
  data: FannieMaeLar89,
});

const FannieMaeDelinquencyReportData = z.object({
  reportType: z.literal(CompanyReportType.FANNIE_MAE_DELINQUENCY),
  data: FannieMaeDelinquencyFile,
});

const FannieMaeDelinquencyEditReportData = z.object({
  reportType: z.literal(CompanyReportType.FANNIE_MAE_DELINQUENCY_EDIT),
  data: FannieMaeDelinquencyFile,
});

const FHATS264ReportData = z.object({
  reportType: z.literal(CompanyReportType.FHA_TS_264),
  data: FHATS264,
});

const FHATS264EditReportData = z.object({
  reportType: z.literal(CompanyReportType.FHA_TS_264_EDIT),
  data: FHATS264,
});

export const InvestorReportingData = z.discriminatedUnion('reportType', [
  FannieMaeLar96DailyReportData,
  FannieMaeLar96MonthlyReportData,
  FannieMaeLar96TrailingReportData,
  FannieMaeLar96EditReportData,
  FannieMaeCRSDraftRequestReportData,
  FannieMaeCRSDraftRequestEditReportData,
  FannieMaeLar32ReportData,
  FannieMaeLar32EditReportData,
  FannieMaeLar81ReportData,
  FannieMaeLar81EditReportData,
  FannieMaeLar83ReportData,
  FannieMaeLar83EditReportData,
  FannieMaeLar89ReportData,
  FannieMaeLar89EditReportData,
  FannieMaeDelinquencyReportData,
  FannieMaeDelinquencyEditReportData,

  FreddieMacLLRDailyReportData,
  FreddieMacLLRMonthlyReportData,
  FreddieMacLLRTrailingReportData,
  FreddieMacLLREditReportData,
  FreddieMacEdrReportData,
  FreddieMacEdrEditReportData,

  GinnieMaeRFSMonthlyReportData,
  GinnieMaeRFSEditReportData,

  FHATS264ReportData,
  FHATS264EditReportData,
]);
export type InvestorReportingData = z.infer<typeof InvestorReportingData>;

export const InvestorReportingSource = z.enum(['user', 'loanEvent', 'scheduledReport']);
export type InvestorReportingSource = z.infer<typeof InvestorReportingSource>;

export const InvestorReporting = z.object({
  id: InvestorReportingId,
  loanId: LoanId,
  loanSnapshotVersion: z.number().optional(),
  companyId: CompanyId,
  eventId: EventId.optional(),
  recurringCompanyReportId: RecurringCompanyReportId.optional(),
  companyReportIds: CompanyReportId.array(),
  investor: GSEName,
  reportType: InvestorReportingReportType,
  data: InvestorReportingData,
  monthYear: ReportingCycle.optional(),
  source: InvestorReportingSource,
  createdAt: zodDateOrString,
  createdByUserId: UserId.optional(),
  deletedAt: zodDateOrString.optional(),
});
export type InvestorReporting = z.infer<typeof InvestorReporting>;
